import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { StampIcon } from '@/components/landing/stamp-icon'

const DOCS: Record<string, { title: string; sections: { id: string; label: string }[] }> = {
  '/legal/terms': {
    title: 'Terms of Service',
    sections: [
      { id: 'agreement', label: 'Agreement to Terms' },
      { id: 'service', label: 'Description of Service' },
      { id: 'eligibility', label: 'Eligibility and Registration' },
      { id: 'acceptable-use', label: 'Acceptable Use' },
      { id: 'billing', label: 'Payment and Billing' },
      { id: 'availability', label: 'Service Availability' },
      { id: 'data-privacy', label: 'Data and Privacy' },
      { id: 'ip', label: 'Intellectual Property' },
      { id: 'termination', label: 'Termination' },
      { id: 'liability', label: 'Limitation of Liability' },
      { id: 'indemnification', label: 'Indemnification' },
      { id: 'governing-law', label: 'Governing Law' },
      { id: 'changes', label: 'Changes to Terms' },
      { id: 'general', label: 'General Provisions' },
      { id: 'contact', label: 'Contact' },
    ],
  },
  '/legal/privacy': {
    title: 'Privacy Policy',
    sections: [
      { id: 'who-we-are', label: 'Who We Are' },
      { id: 'data-collected', label: 'Data We Collect' },
      { id: 'how-used', label: 'How We Use Your Data' },
      { id: 'legal-basis', label: 'Legal Basis (GDPR)' },
      { id: 'storage', label: 'Data Storage and Location' },
      { id: 'sharing', label: 'Data Sharing' },
      { id: 'retention', label: 'Data Retention' },
      { id: 'rights', label: 'Your Rights' },
      { id: 'cookies', label: 'Cookies' },
      { id: 'tracking', label: 'Open and Click Tracking' },
      { id: 'dmarc', label: 'DMARC Reports' },
      { id: 'transfers', label: 'International Transfers' },
      { id: 'children', label: "Children's Privacy" },
      { id: 'changes', label: 'Changes to This Policy' },
      { id: 'contact', label: 'Contact' },
    ],
  },
  '/legal/acceptable-use': {
    title: 'Acceptable Use',
    sections: [
      { id: 'consent', label: 'The Consent Principle' },
      { id: 'streams', label: 'Sending Streams' },
      { id: 'always-allowed', label: 'Always Allowed' },
      { id: 'allowed-conditions', label: 'Allowed with Conditions' },
      { id: 'never-allowed', label: 'Never Allowed' },
      { id: 'prohibited', label: 'Prohibited Content' },
      { id: 'practices', label: 'Sending Practices' },
      { id: 'ramp', label: 'Account Ramp Limits' },
      { id: 'monitoring', label: 'Monitoring and Enforcement' },
      { id: 'infrastructure', label: 'Infrastructure Protection' },
      { id: 'reporting', label: 'Reporting Abuse' },
      { id: 'changes', label: 'Changes to This Policy' },
    ],
  },
  '/legal/dpa': {
    title: 'DPA',
    sections: [
      { id: 'definitions', label: 'Definitions' },
      { id: 'scope', label: 'Scope of Processing' },
      { id: 'controller', label: 'Controller Obligations' },
      { id: 'processor', label: 'Processor Obligations' },
      { id: 'location', label: 'Data Location and Transfers' },
      { id: 'breach', label: 'Data Breach Notification' },
      { id: 'ccpa', label: 'CCPA Service Provider Terms' },
      { id: 'term', label: 'Term and Termination' },
      { id: 'liability', label: 'Liability' },
      { id: 'contact', label: 'Contact' },
      { id: 'annex-1', label: 'Annex 1: Processing Details' },
      { id: 'annex-2', label: 'Annex 2: Security Measures' },
      { id: 'annex-3', label: 'Annex 3: Sub-processors' },
      { id: 'annex-4', label: 'Annex 4: SCCs & UK Addendum' },
    ],
  },
  '/legal/cookies': {
    title: 'Cookie Policy',
    sections: [
      { id: 'what-are-cookies', label: 'What Are Cookies' },
      { id: 'cookies-we-use', label: 'Cookies We Use' },
      { id: 'cookies-we-dont-use', label: "Cookies We Don't Use" },
      { id: 'cookie-banner', label: 'Our Cookie Banner' },
      { id: 'control', label: 'How to Control Cookies' },
      { id: 'changes', label: 'Changes' },
      { id: 'contact', label: 'Contact' },
    ],
  },
  '/legal/subprocessors': {
    title: 'Sub-processors',
    sections: [
      { id: 'current', label: 'Current Sub-processors' },
      { id: 'not-used', label: 'Explicitly NOT Used' },
      { id: 'agreements', label: 'Data Protection Agreements' },
      { id: 'changes', label: 'Changes to Sub-processors' },
      { id: 'archive', label: 'Archive' },
    ],
  },
  '/legal/sla': {
    title: 'SLA',
    sections: [
      { id: 'scope', label: 'Scope' },
      { id: 'uptime', label: 'Uptime Commitments' },
      { id: 'credits', label: 'Service Credits' },
      { id: 'claim', label: 'How to Claim' },
      { id: 'exclusions', label: 'Exclusions' },
      { id: 'not-covered', label: 'What This SLA Does NOT Cover' },
      { id: 'delivery', label: 'Email Delivery Commitment' },
      { id: 'remedy', label: 'Sole Remedy' },
      { id: 'changes', label: 'Changes' },
    ],
  },
}

const ALL_DOCS = [
  { label: 'Terms of Service', href: '/legal/terms' },
  { label: 'Privacy Policy', href: '/legal/privacy' },
  { label: 'Acceptable Use', href: '/legal/acceptable-use' },
  { label: 'DPA', href: '/legal/dpa' },
  { label: 'Cookie Policy', href: '/legal/cookies' },
  { label: 'Sub-processors', href: '/legal/subprocessors' },
  { label: 'SLA', href: '/legal/sla' },
]

function LegalLayout() {
  const location = useLocation()
  const pathname = location.pathname
  const currentDoc = DOCS[pathname]

  return (
    <div style={{
      fontFamily: "var(--oe-font-sans)", background: '#FDF8F3',
      color: '#1a1216', minHeight: '100vh', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <header className="section-pad" style={{
        borderBottom: '1px solid #EDE9E4',
        paddingTop: 20, paddingBottom: 20,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        maxWidth: 1100, margin: '0 auto', width: '100%',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#1a1216' }}>
          <StampIcon size={32} variant="cream" />
          <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em' }}>one-email</span>
        </Link>
        <Link
          to="/"
          style={{ fontSize: 14, fontWeight: 700, color: '#6d6462', textDecoration: 'none' }}
        >
          <span className="desktop-only" style={{ display: 'inline' }}>&larr; Back to home</span>
          <span className="mobile-only" style={{ display: 'inline' }}>&larr; Back</span>
        </Link>
      </header>

      {/* Mobile: horizontal scrollable section nav */}
      {currentDoc && (
        <div className="mobile-only" style={{
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          borderBottom: '1px solid #EDE9E4', padding: '12px 20px',
          display: 'flex', gap: 4,
        }}>
          {currentDoc.sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12,
                fontWeight: 600, color: '#6d6462', textDecoration: 'none',
                whiteSpace: 'nowrap', background: '#f5f2ef',
                flexShrink: 0,
              }}
            >
              {section.label}
            </a>
          ))}
        </div>
      )}

      <div className="section-pad" style={{
        maxWidth: 1100, margin: '0 auto',
        paddingTop: 48, paddingBottom: 80,
        display: 'flex',
        gap: 64,
        flex: 1, width: '100%',
      }}>
        {/* Sidebar — chapters (hidden on mobile, shown as horizontal nav above) */}
        <nav className="desktop-only" style={{ width: 220, flexShrink: 0, position: 'sticky', top: 40, alignSelf: 'flex-start' }}>
          {currentDoc && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#FF6B00',
                letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 16,
              }}>{currentDoc.title}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 32 }}>
                {currentDoc.sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    style={{
                      padding: '7px 12px', borderRadius: 8, fontSize: 13,
                      fontWeight: 600, color: '#6d6462', textDecoration: 'none',
                      transition: 'all 0.2s', lineHeight: 1.4,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fff'
                      e.currentTarget.style.color = '#1a1216'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent'
                      e.currentTarget.style.color = '#6d6462'
                    }}
                  >
                    {section.label}
                  </a>
                ))}
              </div>
            </>
          )}

          {/* Other docs */}
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#6d6462',
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12,
          }}>Other documents</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {ALL_DOCS.filter((d) => d.href !== pathname).map((doc) => (
              <Link
                key={doc.href}
                to={doc.href}
                style={{
                  padding: '7px 12px', borderRadius: 8, fontSize: 13,
                  fontWeight: 500, color: '#777', textDecoration: 'none',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#6d6462'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#888'
                }}
              >
                {doc.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Content */}
        <main style={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </main>
      </div>

      {/* Mobile: other docs links */}
      <div className="mobile-only" style={{
        padding: '16px 20px', borderTop: '1px solid #EDE9E4',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: '#6d6462',
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4,
        }}>Other documents</div>
        {ALL_DOCS.filter((d) => d.href !== pathname).map((doc) => (
          <Link
            key={doc.href}
            to={doc.href}
            style={{
              fontSize: 14, fontWeight: 500, color: '#777', textDecoration: 'none',
            }}
          >
            {doc.label}
          </Link>
        ))}
      </div>

      {/* Bottom bar */}
      <footer className="section-pad flex-row" style={{
        maxWidth: 1100, margin: '0 auto',
        paddingTop: 24, paddingBottom: 24,
        borderTop: '1px solid #EDE9E4', width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
      }}>
        <span style={{ fontSize: 12, color: '#777' }}>
          &copy; {new Date().getFullYear()} One-Email
        </span>
        <div className="desktop-only" style={{ display: 'flex', gap: 20 }}>
          {ALL_DOCS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              style={{ fontSize: 12, color: '#777', textDecoration: 'none', fontWeight: 500 }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </footer>
    </div>
  )
}

export const Route = createFileRoute('/legal')({
  component: LegalLayout,
})
